import { Injectable } from '@angular/core';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { ConnectionService } from '@services/connection-service';
import { UserService } from '@services/user';
import { AppConfig } from 'client/app/app.config';
import {
  RegimenRehaulV1Experiment,
  RegimenRehaulV1Result,
  RestrictedProductForPregnancyAndTeenagerExperiment,
  RegimenReorderExperiment,
  ReviewOrderPageExperiment,
  RegimenPricingExperiment,
  BottomNavConfigExperiment,
  ShopDynamicBannerExperiment,
  ChatSkipLastQuestionsExperiment,
  RecentlyViewedExperiment,
  RetentionHomeRevampExperiment,
  FollowUpChatDefaultFirstAnswerExperiment,
  PrepaidDiscountExperiment,
  FupQuestionsNewUiExperiment,
  RegimenVariantsScrollViewCollapseExperiment,
  RegimenProductImageCarouselExperiment,
  RegimenPreparationTimerVariant,
  RecommendedProductsExperiment,
  RecommendedProductsSurface,
  ConsultationQuizExperiment,
  MoneyBackGuaranteeClassConfig,
  MoneyBackGuaranteeExperiment,
  QuizQuestionEntry,
  QuizStepCounts,
  ScrollDepthTriggerExperiment,
  UserProfileInputBreakdownExperiment,
  HairAiExperiment,
  AiReportHairExperiment,
  UnpaidUserLlmBotExperiment,
  InvestigationBannerExperiment,
} from './experiment.service.types';
import { RegimenRouteVariant } from 'client/app/user/user-view/user-regimen/regimen.types';
import { TreatmentPlanExperiment } from '@shared/report-treatment-plan/report-treatment-plan.types';
import { ScrollDepthPageName } from '@services/scroll-depth-service/scroll-depth.types';


/**
 * Fallback quiz step totals when the consultation_quiz_ui variant omits stepCounts. Female bands
 * (evaluated by ascending maxAge) are also the default for unknown/Both gender. HAIR-only today.
 */
const QUIZ_DEFAULT_STEP_COUNTS: Record<string, QuizStepCounts> = {
  HAIR: {
    Male: 10,
    Female: [
      { maxAge: 17, count: 13 },
      { maxAge: 45, count: 14 },
      { maxAge: 200, count: 13 },
    ],
  },
};


/** Fallback grid questions when the consultation_quiz_ui variant omits gridQuestionIds. */
const QUIZ_DEFAULT_GRID_QUESTION_IDS: readonly string[] = ['Multiselecthair', 'communicationLanguagePreference'];


/** Server-side window (Shared.FollowUp.NUMBER_OF_DAYS_INSTANT_CHECKUP_TAKEN) the photo gates mirror. */
const QUIZ_DEFAULT_INSTANT_CHECKUP_RECENCY_DAYS = 30;

export interface AcqShopExperiment {
  key: 'acq_shop';
  enable?: boolean;
  variant?: {
    startDay?: number;
    endDay?: number;
    sectionOrder?: Partial<Record<'FACE' | 'HAIR' | 'BOTH', string[]>>;
    bannerProductCards?: boolean;
  };
}


@Injectable({ providedIn: 'root' })
export class ExperimentService {
  appVersion: number = null;
  webAppVersion: string = null;
  private readonly experimentAndroidVersionForParseInterceptor: number = 207000;
  private readonly experimentForVideoInstructions: number = 207141;
  isInternalUser: boolean = false;
  appVersionFromConfigTable: { androidApp: string, webApp: string } = {
    androidApp: null, webApp: null,
  };
  isMulticoncernUI: boolean = false;
  onboardingTrustInfoExperiment: any = null;

  private _experimentsLoadedResolve: () => void;
  readonly experimentsLoaded: Promise<void> = new Promise((resolve) => {
    this._experimentsLoadedResolve = resolve;
  });

  private isiOSDevice: boolean = false;
  private osInformationLoaded: Promise<void> = null;

  // Experiment flags
  applyCashExperiment: boolean = false;
  globalCacheExperiment: any = null; // Store the experiment object for setEnableGlobalCache
  restrictedShopForPregnancyAndTeenagerExperiment: RestrictedProductForPregnancyAndTeenagerExperiment = null;
  addToCartVariantSelection: boolean = false;
  genderTranslationsEnabled: boolean = false;
  optimiseApiEnabled: boolean = false;
  hideIncompleteChatBottomsheet: boolean = false;
  hairPhotoGalleryUpload: boolean = false;
  videoCallExperiment: boolean = false;
  moneyBackGuaranteeExperiment: MoneyBackGuaranteeExperiment = null;
  regimenReorderExperiment: RegimenReorderExperiment = null;
  reviewOrderPageExperiment: ReviewOrderPageExperiment = null;
  shopDynamicBannerExperiment: ShopDynamicBannerExperiment = null;
  prefillAddressShiprocketEnabled: boolean = false;
  prepaidDiscountExperiment: PrepaidDiscountExperiment = null;
  newRegimenPricingExperiment: RegimenPricingExperiment;
  sharePaymentLink: boolean = false;
  bottomNavConfigExperiment: BottomNavConfigExperiment = null;
  npsFeedbackRevamp: boolean = false;
  regimenFaqExplorePostsEnabled: boolean = false;
  isRetentionHomeRevampExperimentEnabled: boolean = false;
  retentionHomeRevampExperiment: RetentionHomeRevampExperiment = null;
  diet1500Experiment: any = null;
  restockBannerExperiment: boolean = false;

  // Regimen route refactor experiment flags
  regimenRouteExperiment: boolean = false;
  regimenRouteVariant: RegimenRouteVariant = {};
  reportInRegimenPageExperiment: boolean = false;
  regimenRehaulV2: boolean = false;
  regimenPreparationTimerVariant: RegimenPreparationTimerVariant = { activationDelayInSeconds: 120 };
  newInstructionUIExperiment: boolean = false;
  instructionsLanguageChangeExperiment: boolean = false;
  dietBannerV2: boolean = false;
  dietPlanV2Experiment: boolean = false;

  // Skin/Hair report experiment flags
  skinReportDrVideoExperiment: any = null;
  skinReportHeroIngredientsVariant: { numberOfIngredients: number } = null;
  excludeInstantCheckupProblems: string[] = [];
  reportAiDetectionClasses: string[] = [];
  treatmentPlanDescriptionExperiment: TreatmentPlanExperiment | null = null;
  followUpReportV2Enabled: boolean = false;
  chatSkipLastQuestionsExperiment: ChatSkipLastQuestionsExperiment = null;
  chatSkipLastQuestionHomeBanner: ChatSkipLastQuestionsExperiment = null;
  consultationQuizExperiment: ConsultationQuizExperiment = null;
  chatSpeechToTextEnabled: boolean = false;
  paidAiChatEnabled: boolean = false;
  acqShop: AcqShopExperiment | null = null;
  recentlyViewedExperiment: RecentlyViewedExperiment = null;
  recommendedProductsExperiment: RecommendedProductsExperiment = null;
  followUpChatDefaultFirstAnswer: FollowUpChatDefaultFirstAnswerExperiment = null;
  fupQuestionsNewUiExperiment: FupQuestionsNewUiExperiment | null = null;
  regimenVariantsScrollViewCollapseExperiment: RegimenVariantsScrollViewCollapseExperiment | null = null;
  regimenProductImageCarousel: RegimenProductImageCarouselExperiment = null;
  idealHairlineEnabled: boolean = false;
  idealHairlineFilledDetections: boolean = false;
  idealHairlineFallingHair: boolean = false;
  hairTopWithScores: boolean = false;
  restartRegimenChatUiExperiment: boolean = false;
  testimonialsDecisionEngineEnabled: boolean = false;
  twinTestimonialsEnabled: boolean = false;
  skinMainConcernChangesEnabled: boolean = false;
  showImprovementTimelineOnReport: boolean = false;
  showImprovementTimelineOnRegimen: boolean = false;
  regimenValuePropV3Enabled: boolean = false;
  scrollDepthTriggerExperiment: ScrollDepthTriggerExperiment | null = null;
  investigationBannerExperiment: InvestigationBannerExperiment | null = null;
  orderFlowHeaderExperiment: boolean = false;
  userProfileInputBreakdownExperiment: UserProfileInputBreakdownExperiment | null = null;
  hairAiExperiment: HairAiExperiment | null = null;
  aiReportHairExperiment: AiReportHairExperiment | null = null;
  unpaidUserLlmBotExperiment: UnpaidUserLlmBotExperiment | null = null;
  regimenValuePropV3: boolean = false;

  constructor(
    private readonly appWebBridge: AppWebBridgeService,
    private readonly windowRef: WindowRefService,
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly userService: UserService,
  ) {
    this.appWebBridge.requestAppVersion().then((version: number) => (this.appVersion = version));
    try {
      this.webAppVersion = this.appConfig.Shared.AppVersion;
      this.isInternalUser = this.conn.isInternalUser();
    } catch (err) {
      // Handle error here
    }
  }

  private ensureOsInformationLoaded(): Promise<void> {
    this.osInformationLoaded ??= this.appWebBridge.isIosDevice()
      .then((isIosDevice: boolean): void => {
        this.isiOSDevice = isIosDevice || this.windowRef.isSafariBrowser();
      })
      .catch((): void => {
        this.isiOSDevice = this.windowRef.isSafariBrowser();
      });
    return this.osInformationLoaded;
  }

  async enableParseInterceptor(): Promise<boolean> {
    await this.ensureOsInformationLoaded();
    if (this.isiOSDevice) return true;
    if (this.isInternalUser) return true;
    if (this.appVersion >= this.experimentAndroidVersionForParseInterceptor) return true;
    return false;
  }

  get showNewInstructionVideo(): boolean {
    return this.appVersion >= this.experimentForVideoInstructions;
  }

  /**
   * Fetches and processes active user experiments
   * This function can be used to centralize experiment handling logic
   * @param username Optional username to fetch experiments for
   * @returns Promise<any[]> The fetched experiments array
   */
  async fetchActiveUserExperiment(username?: string): Promise<any[]> {
    try {
      const experiments = await this.conn.findUserActiveExperiments(username);
      await this.ensureOsInformationLoaded();
      // Reset experiment flags
      this.resetExperimentFlags();

      // Process experiments
      experiments?.forEach((experiment: any): void => {
        this.processExperiment(experiment);
      });

      this._experimentsLoadedResolve();
      return experiments || [];
    } catch (error) {
      this._experimentsLoadedResolve();
      return [];
    }
  }

  markExperimentsLoaded(): void {
    this._experimentsLoadedResolve();
  }

  /**
   * Resets all experiment flags to their default values
   */
  private resetExperimentFlags(): void {
    this.applyCashExperiment = false;
    this.globalCacheExperiment = null;
    this.addToCartVariantSelection = false;
    this.videoCallExperiment = false;
    this.moneyBackGuaranteeExperiment = null;
    this.regimenReorderExperiment = null;
    this.reviewOrderPageExperiment = null;
    this.skinReportDrVideoExperiment = null;
    this.skinReportHeroIngredientsVariant = null;
    this.excludeInstantCheckupProblems = [];
    this.reportAiDetectionClasses = [];
    this.treatmentPlanDescriptionExperiment = null;
    this.followUpReportV2Enabled = false;
    this.prefillAddressShiprocketEnabled = false;
    this.newRegimenPricingExperiment = undefined;
    this.bottomNavConfigExperiment = null;
    this.shopDynamicBannerExperiment = null;
    this.npsFeedbackRevamp = false;
    this.isRetentionHomeRevampExperimentEnabled = false;
    this.retentionHomeRevampExperiment = null;
    this.diet1500Experiment = null;
    this.restockBannerExperiment = false;
    this.acqShop = null;
    this.recentlyViewedExperiment = null;
    this.followUpChatDefaultFirstAnswer = null;
    this.prepaidDiscountExperiment = null;
    this.fupQuestionsNewUiExperiment = null;
    this.regimenRouteExperiment = false;
    this.regimenRouteVariant = {};
    this.reportInRegimenPageExperiment = false;
    this.regimenRehaulV2 = false;
    this.regimenPreparationTimerVariant = { activationDelayInSeconds: 120 };
    this.newInstructionUIExperiment = false;
    this.instructionsLanguageChangeExperiment = false;
    this.dietBannerV2 = false;
    this.dietPlanV2Experiment = false;
    this.regimenVariantsScrollViewCollapseExperiment = null;
    this.regimenProductImageCarousel = null;
    this.idealHairlineEnabled = false;
    this.idealHairlineFilledDetections = false;
    this.idealHairlineFallingHair = false;
    this.hairTopWithScores = false;
    this.restartRegimenChatUiExperiment = false;
    this.regimenFaqExplorePostsEnabled = false;
    this.testimonialsDecisionEngineEnabled = false;
    this.twinTestimonialsEnabled = false;
    this.showImprovementTimelineOnReport = false;
    this.showImprovementTimelineOnRegimen = false;
    this.regimenValuePropV3Enabled = false;
    this.onboardingTrustInfoExperiment = null;
    this.recommendedProductsExperiment = null;
    this.skinMainConcernChangesEnabled = false;
    this.consultationQuizExperiment = null;
    this.chatSpeechToTextEnabled = false;
    this.paidAiChatEnabled = false;
    this.scrollDepthTriggerExperiment = null;
    this.investigationBannerExperiment = null;
    this.orderFlowHeaderExperiment = false;
    this.hairPhotoGalleryUpload = false;
    this.userProfileInputBreakdownExperiment = null;
    this.hairAiExperiment = null;
    this.aiReportHairExperiment = null;
    this.unpaidUserLlmBotExperiment = null;
    this.regimenValuePropV3 = false;
  }

  /**
   * Processes individual experiment and sets appropriate flags
   * @param experiment The experiment object to process
   */
  private processExperiment(experiment: any): void {
    switch (experiment.key) {
      case 'apply_cash':
        this.applyCashExperiment = true;
        break;
      case 'enable_global_cache':
        this.globalCacheExperiment = experiment; // Store the full experiment object
        break;
      case 'add_to_cart_variant_selection':
        this.addToCartVariantSelection = true;
        break;
      case 'shop_restricted_product_pregnancy_teenager':
        this.restrictedShopForPregnancyAndTeenagerExperiment = experiment;
        break;
      case 'gender_translations':
        this.genderTranslationsEnabled = true;
        break;
      case 'optimise_paid_home_page':
        this.optimiseApiEnabled = true;
        break;
      case 'hide_incomplete_chat_bottomsheet':
        this.hideIncompleteChatBottomsheet = true;
        break;
      case 'hair_photo_gallery_upload':
        this.hairPhotoGalleryUpload = true;
        break;
      case 'enable_hair_ai':
        this.hairAiExperiment = experiment;
        break;
      case '3000_video_call':
        this.videoCallExperiment = true;
        break;
      case 'money_back_guarantee':
        this.moneyBackGuaranteeExperiment = experiment;
        break;
      case 'skin_report_dr_video':
        this.skinReportDrVideoExperiment = experiment;
        break;
      case 'skin_report_hero_ingredients':
        this.skinReportHeroIngredientsVariant = experiment?.variant;
        break;
      case 'followUp_report_v2':
        this.followUpReportV2Enabled = true;
        break;
      case 'exclude_instant_checkup_problems':
        this.excludeInstantCheckupProblems = experiment?.variant || [];
        break;
      case 'skin_hair_report_ai_detections':
        this.reportAiDetectionClasses = experiment?.variant?.classes ?? [];
        break;
      case 'treatment_plan_description':
        this.treatmentPlanDescriptionExperiment = experiment;
        break;
      case 'kit_reorder':
        this.regimenReorderExperiment = experiment;
        break;
      case 'review_order_page':
        this.reviewOrderPageExperiment = experiment;
        break;
      case 'prefill_address_shiprocket':
        this.prefillAddressShiprocketEnabled = true;
        break;
      case 'prepaid_discount':
        this.prepaidDiscountExperiment = experiment;
        break;
      case 'add_extra_regimen_products':
        this.newRegimenPricingExperiment = experiment;
        break;
      case 'share_payment_link':
        this.sharePaymentLink = true;
        break;
      case 'ret_homepage_revamp':
        this.isRetentionHomeRevampExperimentEnabled = true;
        this.retentionHomeRevampExperiment = experiment;
        break;
      case 'diet_1500':
        this.diet1500Experiment = experiment;
        break;
      case 'restock_banner':
        this.restockBannerExperiment = true;
        break;
      case 'bottom_nav_config':
        this.bottomNavConfigExperiment = experiment;
        break;
      case 'shop_dynamic_banner':
        this.shopDynamicBannerExperiment = experiment;
        break;
      case 'scroll_depth_trigger':
        this.scrollDepthTriggerExperiment = experiment;
        break;
      case 'user_profile_input_breakdown':
        this.userProfileInputBreakdownExperiment = experiment;
        break;
      case 'ai_report_hair':
        this.aiReportHairExperiment = experiment;
        break;
      case 'regimen_route_refactor':
        this.regimenRouteExperiment = true;
        this.regimenRouteVariant = (experiment.variant as RegimenRouteVariant) || {};
        break;
      case 'report_in_regimen_page':
        this.reportInRegimenPageExperiment = true;
        break;
      case 'regimen_rehaul_v2':
        this.regimenRehaulV2 = true;
        break;
      case 'tree_personalized_regimen_activation':
        this.regimenPreparationTimerVariant = experiment.variant as RegimenPreparationTimerVariant;
        break;
      case 'instruction_page_redesign':
        this.newInstructionUIExperiment = true;
        break;
      case 'instructions_language_change':
        this.instructionsLanguageChangeExperiment = true;
        break;
      case 'diet_banner_v2':
        this.dietBannerV2 = true;
        break;
      case 'diet_plan_v2':
        this.dietPlanV2Experiment = true;
        break;
      case 'chat_skip_last_questions':
        this.chatSkipLastQuestionsExperiment = experiment;
        break;
      case 'chat_skip_last_questions_home_banner':
        this.chatSkipLastQuestionHomeBanner = experiment;
        break;
      case 'recently_viewed_visibility':
        this.recentlyViewedExperiment = experiment;
        break;
      case 'followup_chat_default_first_answer':
        this.followUpChatDefaultFirstAnswer = experiment;
        break;
      case 'fup_questions_new_UI':
        this.fupQuestionsNewUiExperiment = experiment;
        break;
      case 'consultation_quiz_ui':
        this.consultationQuizExperiment = experiment;
        break;
      case 'chat_speech_to_text':
        this.chatSpeechToTextEnabled = true;
        break;
      case 'enable_paid_ai_chat':
        this.paidAiChatEnabled = true;
        break;
      case 'acq_shop':
        this.acqShop = experiment;
        break;
      case 'NPS_feedback_revamp':
        this.npsFeedbackRevamp = true;
        break;
      case 'regimen_variants_scroll_view_collapse':
        this.regimenVariantsScrollViewCollapseExperiment = {
          hideAfterTopScroll: !!experiment?.variant?.hideAfterTopScroll,
        };
        break;
      case 'regimen_product_image_carousel':
        this.regimenProductImageCarousel = experiment;
        break;
      case 'ideal_hairline':
        this.idealHairlineEnabled = true;
        this.idealHairlineFilledDetections = experiment?.variant?.filledDetections ?? false;
        this.idealHairlineFallingHair = experiment?.variant?.fallingHair ?? false;
        break;
      case 'hair_top_with_scores':
        this.hairTopWithScores = true;
        break;
      case 'restart_regimen_chat_UI':
        this.restartRegimenChatUiExperiment = true;
        break;
      case 'regimen_FAQ_explore_posts':
        this.regimenFaqExplorePostsEnabled = true;
        break;
      case 'testimonials_decision_engine':
        this.testimonialsDecisionEngineEnabled = true;
        break;
      case 'twin_testimonials':
        this.twinTestimonialsEnabled = true;
        break;
      case 'improvement_timeline':
        this.showImprovementTimelineOnReport = !!experiment.variant?.report;
        this.showImprovementTimelineOnRegimen = !!experiment.variant?.regimen;
        break;
      case 'regimen_value_prop_v3':
        this.regimenValuePropV3Enabled = true;
        this.regimenValuePropV3 = true;
        break;
      case 'onboarding-trust-info':
        this.onboardingTrustInfoExperiment = experiment;
        break;
      case 'recommended_products':
        this.recommendedProductsExperiment = experiment;
        break;
      case 'skin_main_concern_changes':
        this.skinMainConcernChangesEnabled = true;
        break;
      case 'order_flow_header':
        this.orderFlowHeaderExperiment = true;
        break;
      case 'unpaid_user_llm_bot':
        this.unpaidUserLlmBotExperiment = experiment;
        break;
      case 'investigation_banner':
        this.investigationBannerExperiment = experiment;
        break;
      default:
        break;
    }
  }

  getAcqShopExperiment(): AcqShopExperiment | null {
    return this.acqShop;
  }

  getOnboardingTrustInfoVariant(): any {
    return this.onboardingTrustInfoExperiment?.variant ?? null;
  }

  isOnboardingTrustInfoEnabledForClass(concernClass: string): boolean {
    const classData = this.getOnboardingTrustInfoVariant()?.[concernClass];
    return !!classData && Object.keys(classData).length > 0;
  }

  getRestrictedShopExperiment(): RestrictedProductForPregnancyAndTeenagerExperiment {
    return this.restrictedShopForPregnancyAndTeenagerExperiment ?? null;
  }

  getChatSkipLastQuestionsExperiment() {
    return this.chatSkipLastQuestionsExperiment ?? null;
  }

  getChatSkipLastQuestionHomeBanner() {
    return this.chatSkipLastQuestionHomeBanner ?? null;
  }

  getRecentlyViewedExperiment(): RecentlyViewedExperiment {
    return this.recentlyViewedExperiment ?? null;
  }

  // Gated on the experiment being assigned; a valid variant further limits it to a per-surface day-window.
  isRecommendedProductsSectionVisible(user: any, surface: RecommendedProductsSurface, daysSinceDelivery: number | null): boolean {
    if (!this.userService.isRetentionUserOnRegimen(user)) return false;
    if (!this.recommendedProductsExperiment) return false;

    const variant = this.recommendedProductsExperiment.variant;
    if (!variant) return true;

    const startDay = variant[`${surface}-start-day`];
    const endDay = variant[`${surface}-end-day`];
    if (typeof startDay !== 'number' || typeof endDay !== 'number') return true;

    if (daysSinceDelivery === null) return false;
    return daysSinceDelivery >= startDay && daysSinceDelivery <= endDay;
  }

  isInvestigationBannerVisibleOnHome(daysSinceDelivery: number | null): boolean {
    if (!this.investigationBannerExperiment) return false;
    const variant = this.investigationBannerExperiment.variant;
    if (!variant) return true;
    if (typeof variant.startDay !== 'number' || typeof variant.endDay !== 'number') return true;
    if (daysSinceDelivery === null) return false;
    return daysSinceDelivery >= variant.startDay && daysSinceDelivery <= variant.endDay;
  }

  isProductImageCarouselEnabledForClass(regimenClass: string): boolean {
    const variant = this.regimenProductImageCarousel?.variant;
    if (!variant) {
      return false;
    }
    return !!variant[regimenClass as keyof typeof variant];
  }

  /** Hair wash-care products (shampoo / conditioner / oil) move to the end of the regimen product list. */
  shouldReorderWashCareProducts(regimenClass?: string): boolean {
    return regimenClass === this.appConfig.Shared.Regimen.Class.HAIR;
  }

  /**
   * What `money_back_guarantee` configures for a regimen class: the plan variants it covers and
   * whether /review-order advertises it. Who actually qualifies is a domain rule —
   * `RegimenService.isMoneyBackGuaranteeApplicable`.
   */
  getMoneyBackGuaranteeConfigForClass(regimenClass: string): MoneyBackGuaranteeClassConfig | null {
    const variant = this.moneyBackGuaranteeExperiment?.variant;
    if (!this.moneyBackGuaranteeExperiment?.enable || !variant) return null;
    if (regimenClass === this.appConfig.Shared.Regimen.Class.HAIR) {
      return { variantIds: variant.HAIR || [], shouldShowOnReviewOrderPage: !!variant.regimen_review_page_hair };
    }
    if (regimenClass === this.appConfig.Shared.Regimen.Class.FACE) {
      return { variantIds: variant.FACE || [], shouldShowOnReviewOrderPage: !!variant.regimen_review_page_skin };
    }
    return null;
  }

  isConsultationQuizEnabled(): boolean {
    return !!this.consultationQuizExperiment?.enable
      && !!this.consultationQuizExperiment?.variant?.classes?.length;
  }

  isConsultationQuizEnabledForClass(concernClass: string): boolean {
    if (!this.isConsultationQuizEnabled()) return false;
    return this.consultationQuizExperiment.variant.classes.includes(concernClass);
  }

  /**
   * Fixed number of quiz questions for the progress header, by class + user gender/age. Reads the
   * variant's stepCounts (retunable without a release), falling back to QUIZ_DEFAULT_STEP_COUNTS.
   * Unknown/Both gender uses the Female bands (matches getUserGender's default). Always >= 1.
   */
  getConsultationQuizStepCount(concernClass: string): number {
    const counts = this.consultationQuizExperiment?.variant?.stepCounts?.[concernClass]
      || QUIZ_DEFAULT_STEP_COUNTS[concernClass];
    if (!counts) return 1;
    if (this.userService.getUserGender() === this.appConfig.Shared.Gender.MALE) {
      return Math.max(counts.Male || 1, 1);
    }
    const age = Number(this.conn.getCurrentUser()?.get('Age')) || 0;
    const band = counts.Female?.find((entry) => age <= entry.maxAge) || counts.Female?.[counts.Female.length - 1];
    return Math.max(band?.count || 1, 1);
  }

  getConsultationQuizQuestionEntries(concernClass: string): QuizQuestionEntry[] {
    const entries = this.consultationQuizExperiment?.variant?.questionIds?.[concernClass];
    if (!entries) return [];
    if (this.userService.getUserGender() === this.appConfig.Shared.Gender.MALE) {
      return entries.Male || [];
    }
    return entries.Female || [];
  }

  isFrontendDrivenQuizEnabled(concernClass: string): boolean {
    if (!this.isConsultationQuizEnabledForClass(concernClass)) return false;
    return this.getConsultationQuizQuestionEntries(concernClass).length > 0;
  }

  isQuizProgressCountHidden(): boolean {
    return !!this.consultationQuizExperiment?.variant?.hideProgressCount;
  }

  /** Question ids the quiz renders as a 2-up option grid instead of the default vertical stack. */
  getQuizGridQuestionIds(): readonly string[] {
    const questionIds = this.consultationQuizExperiment?.variant?.gridQuestionIds;
    return questionIds?.length ? questionIds : QUIZ_DEFAULT_GRID_QUESTION_IDS;
  }

  isSingleStepBackEnabled(): boolean {
    return !!this.consultationQuizExperiment?.variant?.singleStepBack;
  }

  shouldAskConcernSpecificQuestionFirst(): boolean {
    return !!this.consultationQuizExperiment?.variant?.showConcernSpecificQuestionFirst;
  }

  isQuizSkipPhotoInstructionsEnabled(): boolean {
    return !!this.consultationQuizExperiment?.variant?.skipPhotoInstructions;
  }

  getPhotoUploadedToastQuestionIds(concernClass: string): string[] {
    return this.consultationQuizExperiment?.variant?.photoUploadedToastQuestionIds?.[concernClass] || [];
  }

  /** Window the quiz's photo gates treat an InstantCheckup as recent. Mirrors the server's tree rule. */
  getInstantCheckupRecencyDays(): number {
    return this.consultationQuizExperiment?.variant?.instantCheckupRecencyDays
      || QUIZ_DEFAULT_INSTANT_CHECKUP_RECENCY_DAYS;
  }

  /**
   * Optional bot-reply poll seed for the quiz, from the consultation_quiz_ui variant's botReplyDelay.
   * Null keeps ChatService's default (1000/1000 → 2s first poll). Lets ops dial the quiz's
   * "typing" delay separately from chatV2 without a release.
   */
  getConsultationQuizBotReplySeed(): { interval1: number; interval2: number } | null {
    const delay = this.consultationQuizExperiment?.variant?.botReplyDelay;
    if (!delay) return null;
    return { interval1: delay.t1, interval2: delay.t2 };
  }

  shouldTriggerAiReportAnalysis(questionId: string, regimenClass: string): boolean {
    if (regimenClass !== this.appConfig.Shared.Regimen.Class.HAIR) return false;
    if (!this.aiReportHairExperiment?.enable) return false;
    const reportTrigger = this.aiReportHairExperiment?.variant?.triggerAtQuestionId;
    if (!reportTrigger) return false;
    const trigger = (this.isFrontendDrivenQuizEnabled(regimenClass)
      && this.consultationQuizExperiment?.variant?.triggerAtQuestionId) || reportTrigger;
    return !!questionId && questionId === trigger;
  }

  isChatSpeechToTextEnabled(): boolean {
    return this.chatSpeechToTextEnabled;
  }

  isPaidAiChatEnabled(): boolean {
    return this.paidAiChatEnabled;
  }

  /** `regimen_value_prop_v3`: render the new <cure-plan-includes> UI-library composite for the
   *  regimen value proposition; disabled falls back to the legacy hand-rolled template. */
  isRegimenValuePropV3Enabled(): boolean {
    return this.regimenValuePropV3;
  }

  isTwinTestimonialsEnabled(): boolean {
    return this.twinTestimonialsEnabled;
  }

  getRetentionHomeRevampExperiment(): RetentionHomeRevampExperiment {
    return this.retentionHomeRevampExperiment ?? null;
  }

  // diet_1500: PRO diet plan pricing. Returns null when the experiment isn't active.
  getDietPlanProPricing(): { proPlanId: string; proPlanPrice: number } | null {
    const variant: any = this.diet1500Experiment?.variant;
    if (!variant) return null;
    const parsed: any = typeof variant === 'string' ? JSON.parse(variant) : variant;
    return { proPlanId: parsed?.proPlanId, proPlanPrice: parsed?.proPlanPrice };
  }

  processRegimenRehaulV1Experiment(
    experiment: RegimenRehaulV1Experiment,
    options?: { regimenClass?: string; userLanguage?: string; }
  ): RegimenRehaulV1Result {
    if (!experiment || !experiment.variant) {
      return {};
    }

    const result: RegimenRehaulV1Result = {};
    const variants = experiment.variant;

    if (variants.back_nudge_dr_call !== undefined) {
      result.nudgeDoctorCallOnBack = variants.back_nudge_dr_call;
    }

    if (variants.dr_banner_change !== undefined) {
      result.drBannerChange = variants.dr_banner_change;
    }

    if (variants.remove_variant !== undefined) {
      result.removeVariants = variants.remove_variant;
    }

    return result;
  }

  /**
   * Calculates whether to use CureSkin Cash based on experiment and user orderState
   * - If applyCashExperiment is false: always return true
   * - If applyCashExperiment is true:
   *   - If orderState is DELIVERED: return false
   *   - Otherwise: return true
   * @param orderState The user's orderState (e.g., 'DELIVERED', 'PROCESSING', etc.)
   * @returns boolean indicating whether to use CureSkin Cash
   */
  shouldUseCureSkinCash(orderState?: string): boolean {
    // If experiment is off, always use cash
    if (!this.applyCashExperiment) {
      return true;
    }

    // If experiment is on, check orderState
    // If orderState is DELIVERED, don't use cash
    if (orderState === this.appConfig.Shared.User.OrderState.DELIVERED) {
      return false;
    }

    // For any other orderState, use cash
    return true;
  }

  getBottomNavConfig(): BottomNavConfigExperiment {
    return this.bottomNavConfigExperiment;
  }

  isBottomNavConfigActive(): boolean {
    const exp = this.bottomNavConfigExperiment;
    return !!(exp?.enable && exp?.variant);
  }

  get hairPhotoGalleryUploadEnabled(): boolean {
    return this.hairPhotoGalleryUpload;
  }

  getSkinReportDrVideoExperiment(): any {
    return this.skinReportDrVideoExperiment;
  }

  getSkinReportHeroIngredientsVariant(): { numberOfIngredients: number } {
    return this.skinReportHeroIngredientsVariant;
  }

  isFollowUpReportV2Enabled(): boolean {
    return this.followUpReportV2Enabled;
  }

  getExcludeInstantCheckupProblems(): string[] {
    return this.excludeInstantCheckupProblems;
  }

  getReportAiDetectionClasses(): string[] {
    return this.reportAiDetectionClasses;
  }

  getShopDynamicBannerExperiment(): ShopDynamicBannerExperiment {
    return this.shopDynamicBannerExperiment ?? null;
  }

  getFollowUpChatDefaultFirstAnswer(): FollowUpChatDefaultFirstAnswerExperiment {
    return this.followUpChatDefaultFirstAnswer ?? null;
  }

  isFupQuestionsNewUiEnabled(): boolean {
    return !!this.fupQuestionsNewUiExperiment?.enable;
  }

  getFupQuestionsNewUiExperiment(): FupQuestionsNewUiExperiment | null {
    return this.fupQuestionsNewUiExperiment ?? null;
  }

  isNpsFeedbackRevampEnabled(): boolean {
    return this.npsFeedbackRevamp;
  }

  isDietPlanV2Enabled(): boolean {
    return this.dietPlanV2Experiment;
  }

  isTestimonialsDecisionEngineEnabled(): boolean {
    return this.testimonialsDecisionEngineEnabled;
  }

  isOrderFlowHeaderEnabled(): boolean {
    return this.orderFlowHeaderExperiment;
  }

  isScrollDepthTriggerEnabled(pageName: ScrollDepthPageName): boolean {
    if (!this.scrollDepthTriggerExperiment) return false;
    const pageNames = this.scrollDepthTriggerExperiment.variant?.pageNames;
    if (!pageNames?.length) return true;
    return pageNames.includes(pageName);
  }

  /** Whether the onboarding profile step is split into separate gender / name / age pages. */
  isUserProfileInputBreakdownEnabled(): boolean {
    if (this.isiOSDevice) return false;
    return !!this.userProfileInputBreakdownExperiment?.enable;
  }

  /** true = selecting a gender routes straight on; false = reveal a Continue CTA first. */
  shouldSkipGenderCta(): boolean {
    if (!this.isUserProfileInputBreakdownEnabled()) return false;
    return !!this.userProfileInputBreakdownExperiment?.variant?.skip_gender_cta;
  }

  /** true = the age page is never shown and Age is left uncollected. */
  shouldSkipAgePage(): boolean {
    if (!this.isUserProfileInputBreakdownEnabled()) return false;
    return !!this.userProfileInputBreakdownExperiment?.variant?.skip_age_page;
  }

  isHairAiEnabled(): boolean {
    return !!this.hairAiExperiment?.enable;
  }

  getUnpaidUserLlmBotExperiment(): UnpaidUserLlmBotExperiment | null {
    return this.unpaidUserLlmBotExperiment;
  }

  getHairAiPhotoTypesForGender(gender: string): string[] | null {
    if (!this.isHairAiEnabled()) return null;
    const photoTypesForGender = this.hairAiExperiment.variant?.[gender?.toLowerCase()];
    if (Array.isArray(photoTypesForGender)) return photoTypesForGender;
    return photoTypesForGender?.values ?? [];
  }
}
